// types/table.types.ts
import type { CSSProperties, ReactNode } from 'react'
import type {
  Column,
  ColumnDef,
  PaginationState,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  RowSelectionState,
  TableOptions,
  OnChangeFn,
  Row,
  Table
} from '@tanstack/react-table'

export enum ScrollMode {
  PAGINATION = 'PAGINATION',
  INFINITE = 'INFINITE'
}

/** Body cell border layout: row lines, column lines, full grid, or none. */
export type TableCellBorders = 'none' | 'horizontal' | 'vertical' | 'both'

/** Header bottom separator only (not affected by `cellBorders`). */
export type TableHeaderBorders = 'horizontal' | 'none'

/** Optional `meta` on `ColumnDef` — consumed by GenericTableV2 */
export type GenericTableColumnMeta = {
  /** Merged onto `<th>` (e.g. override padding for narrow index columns) */
  headerCellClassName?: string
  /** Merged onto `<td>` */
  bodyCellClassName?: string
}

export interface PaginationConfig {
  pageIndex: number
  pageSize: number
  totalRows?: number
  hasNextPage?: boolean
  onPaginationChange?: OnChangeFn<PaginationState>
  pageSizeOptions?: { value: number; label: string }[]
  pageCount?: number
  contentLeftPadding?: number
  contentRightPadding?: number
  fixedBottom?: boolean
}

export interface InfiniteScrollConfig {
  hasMore: boolean
  onLoadMore: () => void
  isLoading?: boolean
  /** Shown below rows while loading the next page (infinite scroll append). */
  loadingMessage?: string
  threshold?: number
}

export interface SortingConfig {
  state: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  manualSorting?: boolean
}

export interface FilteringConfig {
  state: ColumnFiltersState
  onFilteringChange?: OnChangeFn<ColumnFiltersState>
  manualFiltering?: boolean
}

export interface RowSelectionConfig {
  state: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  enableMultiRowSelection?: boolean
}

export interface ColumnVisibilityConfig {
  state: VisibilityState
  onVisibilityChange?: OnChangeFn<VisibilityState>
}

export interface CustomizationConfig {
  header?: {
    hideHeader?: boolean
  }
}

export interface OverlayRowSelectionConfig<T extends object> {
  renderOverlayHeader: (table: Table<T>) => ReactNode
  renderOverlayCell: (row: Row<T>) => ReactNode
}

export interface GenericTableProps<T extends object> {
  data: T[]
  columns: ColumnDef<T>[]
  scrollMode?: ScrollMode
  pagination?: PaginationConfig
  infiniteScroll?: InfiniteScrollConfig
  sorting?: SortingConfig
  filtering?: FilteringConfig
  rowSelection?: RowSelectionConfig
  columnVisibility?: ColumnVisibilityConfig
  tableOptions?: Partial<TableOptions<T>>
  isLoading?: boolean
  customizationConfig?: CustomizationConfig
  onRowClick?: (rowData: T) => void
  getRowId?: (row: T, index: number) => string
  selectedRowId?: string
  /** Row to transiently highlight (e.g. a freshly created row). */
  highlightedRowId?: string
  /** Zero-width leading column for absolutely positioned row controls (e.g. checkboxes). */
  overlayRowSelection?: OverlayRowSelectionConfig<T>
  /** Sticky header offset — only applied when set. */
  stickyHeaderTop?: number
  /** Skip inner scroll; parent container handles overflow. */
  scrollInParent?: boolean
  /**
   * Body cell border layout (header is excluded).
   * - `horizontal` — row dividers only
   * - `vertical` — column dividers only
   * - `both` — full cell grid
   * - `none` — no cell borders (default)
   */
  cellBorders?: TableCellBorders
  /** Header bottom line only. Independent of `cellBorders`. Default `horizontal`. */
  headerBorders?: TableHeaderBorders
  /** Rounded row corners on first/last cells. Default `true`; pass `false` to disable. */
  roundedRows?: boolean
  /** Applied to the `<table>` element (e.g. frozen width on drawer open). */
  tableStyle?: CSSProperties
  tableLayout?: 'auto' | 'fixed'
  /** Override default column width style per cell/header. */
  getColumnStyle?: (columnId: string, column: Column<T, unknown>) => CSSProperties | undefined
  backgroundTransparent?: boolean
  /** Pagination mode: `table-auto` instead of `table-fixed`; colgroup still applied so split thead/tbody tables stay aligned */
  tableLayoutAuto?: boolean
  /**
   * Pagination mode: optional page chrome (title, filters) rendered above the table header inside the
   * same sticky top stack as the thead (`bg-zinc-50/50 backdrop-blur-[10px]`). Scroll should live on an ancestor.
   */
  pageHeader?: ReactNode
}
